import { z } from 'zod';
/** Обсуждения и уведомления (фаза 5). Текст комментария — обычный текст, упоминания — отдельным списком id. */
export const COMMENT_MAX = 4000;
/** assign — назначили исполнителем задачи, due — сегодня срок задачи (фаза 6). */
export const NOTIFICATION_KINDS = ['mention', 'comment', 'invite', 'assign', 'due'];
/** Якорь ветки: блок документа или узел карты/доски. Без якоря — ветка ко всему файлу. */
export const commentAnchorSchema = z
    .object({
    blockId: z.string().min(1).max(64),
    nodeId: z.string().min(1).max(64),
    /** Цитата выделенного текста — показать, к чему относилась ветка, даже если блок удалили. */
    quote: z.string().max(300),
})
    .partial()
    .refine((a) => !(a.blockId && a.nodeId), 'Якорь — блок или узел, не оба');
const body = z.string().trim().min(1, 'Пустой комментарий').max(COMMENT_MAX);
const mentions = z.array(z.uuid()).max(20);
export const createCommentSchema = z.object({
    body,
    /** Ответ в ветке: id корневого комментария. */
    parentId: z.uuid().nullable().default(null),
    mentions: mentions.default([]),
    anchor: commentAnchorSchema.nullable().default(null),
});
export const patchCommentSchema = z
    .object({ body, mentions, resolved: z.boolean() })
    .partial()
    .refine((p) => Object.keys(p).length > 0, 'Нечего менять');
export const notificationsQuerySchema = z.object({
    filter: z.enum(['all', 'unread', 'mentions']).default('all'),
    /** Курсор — id последнего полученного уведомления (uuidv7 упорядочены по времени). */
    before: z.uuid().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
export const readNotificationsSchema = z.union([
    z.object({ ids: z.array(z.uuid()).min(1).max(200) }),
    z.object({ all: z.literal(true) }),
]);
/** Упоминания людей в тексте комментария: `@Имя Фамилия` или `@Имя` (если имя в спейсе одно). */
export function findMentions(text, people) {
    const out = new Set();
    const norm = (s) => s.toLocaleLowerCase('ru').replace(/ё/g, 'е');
    const t = norm(text);
    // длинные имена сначала: «@Анна Котова» не должно сработать как «@Анна» другой Анны
    const full = [...people].sort((a, b) => b.name.length - a.name.length);
    const taken = [];
    const free = (i, n) => taken.every(([a, b]) => i + n <= a || i >= b);
    const boundary = (i) => i >= t.length || !/[\p{L}\p{N}_]/u.test(t[i]);
    /** Перед @ — не буква и не цифра (иначе это почта: ilya@example.com). */
    const start = (i) => i === 0 || !/[\p{L}\p{N}._-]/u.test(t[i - 1]);
    for (const p of full) {
        const name = norm(p.name.trim());
        if (!name)
            continue;
        for (let i = t.indexOf('@' + name); i >= 0; i = t.indexOf('@' + name, i + 1)) {
            if (start(i) && boundary(i + name.length + 1) && free(i, name.length + 1)) {
                out.add(p.id);
                taken.push([i, i + name.length + 1]);
            }
        }
    }
    const byFirst = new Map();
    for (const p of people) {
        const first = norm(p.name.trim().split(/\s+/)[0] ?? '');
        if (first)
            byFirst.set(first, [...(byFirst.get(first) ?? []), p.id]);
    }
    for (const m of t.matchAll(/@([\p{L}][\p{L}\p{N}_-]*)/gu)) {
        const i = m.index;
        if (!start(i) || !free(i, m[0].length))
            continue;
        const ids = byFirst.get(m[1]);
        if (ids?.length === 1)
            out.add(ids[0]);
    }
    return [...out];
}
/** Что произошло — для «Входящих» и писем (без рода: «упоминает», а не «упомянул(а)»). */
export function notificationAction(kind, inComment, locale = 'ru') {
    const ru = locale === 'ru';
    if (kind === 'mention')
        return inComment
            ? ru
                ? 'упоминает вас в обсуждении'
                : 'mentioned you in a discussion'
            : ru
                ? 'упоминает вас в документе'
                : 'mentioned you in a document';
    if (kind === 'comment')
        return ru ? 'отвечает в обсуждении' : 'replied in a discussion';
    if (kind === 'assign')
        return ru ? 'назначает вам задачу' : 'assigned you a task';
    if (kind === 'due')
        return ru ? 'напоминает о сроке сегодня' : 'reminds you it is due today';
    return ru ? 'теперь в спейсе' : 'joined the space';
}
/** Кто «говорит» в уведомлении без автора: напоминание о сроке — от Socrat. */
export function notificationWho(kind, actorName, locale = 'ru') {
    if (actorName)
        return actorName;
    if (kind === 'due')
        return 'Socrat';
    return locale === 'ru' ? 'Участник' : 'Someone';
}
//# sourceMappingURL=discussions.js.map